/**
 * RoomStatus 双缓存：房型列表永久，房型信息 1 分钟，平均价按当天
 */

import { getRoomStatusFromThirdParty, getAvgPriceByDate } from '../../../../../service/storeRevenueApi';
import type { RoomStatusData, AvgPriceRefs } from '../../../../../service/storeRevenueApi';
import {
  buildRoomStatusFromListAndData,
  splitFsmItems,
  type RoomTypeStable,
  type RoomInfoDynamic,
  type PriceHistoryPerRoom,
} from '../../../utils/fsmToRoomStatus';
import { getStoreFromLocalStorage, setStoreSliceInLocalStorage } from '../../../utils/storeRevenueLocalStorage';

const ROOM_DATA_TTL_MS = 1 * 60 * 1000;
const HOURLY_PRICE_NODES = 12;
const PRICE_HISTORY_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function roundTwo(v: number): number {
  return Math.round(v * 100) / 100;
}

const priceHistoryCache = new Map<string, Map<number, PriceHistoryPerRoom>>();

function ensurePriceHistoryForKey(key: string): Map<number, PriceHistoryPerRoom> {
  let perHotel = priceHistoryCache.get(key);
  if (!perHotel) {
    perHotel = new Map();
    priceHistoryCache.set(key, perHotel);
  }
  return perHotel;
}

function ensureSlotsForRoom(perHotel: Map<number, PriceHistoryPerRoom>, roomTypeId: number): PriceHistoryPerRoom {
  let slots = perHotel.get(roomTypeId);
  if ((slots?.length ?? 0) !== HOURLY_PRICE_NODES) {
    slots = Array.from({ length: HOURLY_PRICE_NODES }, () => []);
    perHotel.set(roomTypeId, slots);
  }
  return slots!;
}

function appendPriceHistory(key: string, byId: Record<number, RoomInfoDynamic>): void {
  const now = Date.now();
  const slotIndex = new Date().getHours() % HOURLY_PRICE_NODES;
  const cutoff = now - PRICE_HISTORY_MAX_AGE_MS;
  const perHotel = ensurePriceHistoryForKey(key);
  for (const [roomTypeId, d] of Object.entries(byId)) {
    const id = Number(roomTypeId);
    if (Number.isNaN(id)) continue;
    const slots = ensureSlotsForRoom(perHotel, id);
    const price = roundTwo(d.dayPrice ?? 0);
    slots[slotIndex].push({ price, time: now });
    slots[slotIndex] = slots[slotIndex].filter((p) => p.time >= cutoff);
  }
}

function getTodayYYYYMMDD(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function getLastYearTodayAndSevenDaysAgo(): { lastYearToday: string; sevenDaysAgo: string } {
  const d = new Date();
  const lastYear = new Date(d);
  lastYear.setFullYear(d.getFullYear() - 1);
  const seven = new Date(d);
  seven.setDate(d.getDate() - 7);
  const fmt = (x: Date) =>
    `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  return { lastYearToday: fmt(lastYear), sevenDaysAgo: fmt(seven) };
}

const avgPriceRefsCache = new Map<string, AvgPriceRefs>();
const inFlightAvgRefs = new Map<string, Promise<AvgPriceRefs>>();

export async function getAvgPriceRefsForRoomStatus(hotelId: number | string): Promise<AvgPriceRefs> {
  const today = getTodayYYYYMMDD();
  const cacheKeyAvg = `avgPriceRefs:${String(hotelId)}:${today}`;
  const cached = avgPriceRefsCache.get(cacheKeyAvg);
  if (cached) return cached;
  const inFlight = inFlightAvgRefs.get(cacheKeyAvg);
  if (inFlight) return inFlight;
  const promise = (async () => {
    const { lastYearToday: date1, sevenDaysAgo: date2 } = getLastYearTodayAndSevenDaysAgo();
    const [lastYearToday, sevenDaysAgo] = await Promise.all([
      getAvgPriceByDate(hotelId, date1),
      getAvgPriceByDate(hotelId, date2),
    ]);
    const refs: AvgPriceRefs = { lastYearToday, sevenDaysAgo };
    avgPriceRefsCache.set(cacheKeyAvg, refs);
    return refs;
  })().finally(() => { inFlightAvgRefs.delete(cacheKeyAvg); });
  inFlightAvgRefs.set(cacheKeyAvg, promise);
  return promise;
}

const roomTypeListCache = new Map<string, RoomTypeStable[]>();

interface RoomDataEntry {
  byId: Record<number, RoomInfoDynamic>;
  fetchedAt: number;
}
const roomDataCache = new Map<string, RoomDataEntry>();

function cacheKey(hotelId: number | string): string {
  return `roomStatus:${String(hotelId)}`;
}

function isDataValid(entry: RoomDataEntry): boolean {
  return Date.now() - entry.fetchedAt < ROOM_DATA_TTL_MS;
}

const inFlightRoomStatus = new Map<string, Promise<RoomStatusData>>();

/** 冷启动从 localStorage 恢复：若有有效数据（1 分钟内）则同步返回，供 hook 做初始渲染 */
export function getRoomStatusFromLocalStorageSync(hotelId: number | string): RoomStatusData | null {
  const stored = getStoreFromLocalStorage(hotelId)?.roomStatus;
  if (!stored?.data || typeof stored.fetchedAt !== 'number') return null;
  if (Date.now() - stored.fetchedAt > ROOM_DATA_TTL_MS) return null;
  const d = stored.data as RoomStatusData;
  if (!d || typeof d !== 'object' || !Array.isArray(d.dataSource)) return null;
  return d;
}

export async function getRoomStatusWithCache(
  hotelId: number | string,
  fallback: RoomStatusData
): Promise<RoomStatusData> {
  const key = cacheKey(hotelId);
  const list = roomTypeListCache.get(key);
  const dataEntry = roomDataCache.get(key);

  const buildWithRefs = async (): Promise<RoomStatusData> => {
    let refs: AvgPriceRefs;
    try {
      refs = await getAvgPriceRefsForRoomStatus(hotelId);
    } catch {
      refs = {
        lastYearToday: { date: getLastYearTodayAndSevenDaysAgo().lastYearToday, avgPrice: 0 },
        sevenDaysAgo: { date: getLastYearTodayAndSevenDaysAgo().sevenDaysAgo, avgPrice: 0 },
      };
    }
    const priceHistory = priceHistoryCache.get(key);
    return buildRoomStatusFromListAndData(list!, dataEntry!.byId, refs, priceHistory);
  };

  if (dataEntry && isDataValid(dataEntry) && list && list.length > 0) {
    return buildWithRefs();
  }

  const fromLocal = getRoomStatusFromLocalStorageSync(hotelId);
  if (fromLocal != null) return fromLocal;

  const inFlight = inFlightRoomStatus.get(key);
  if (inFlight) return inFlight;

  const promise = (async (): Promise<RoomStatusData> => {
    try {
      const fsmItems = await getRoomStatusFromThirdParty(hotelId);
      if (!fsmItems.length) return fallback;
      const { roomTypeList, byId } = splitFsmItems(fsmItems);
      if (!roomTypeListCache.has(key)) roomTypeListCache.set(key, roomTypeList);
      roomDataCache.set(key, { byId, fetchedAt: Date.now() });
      appendPriceHistory(key, byId);
      let refs: AvgPriceRefs;
      try {
        refs = await getAvgPriceRefsForRoomStatus(hotelId);
      } catch {
        refs = {
          lastYearToday: { date: getLastYearTodayAndSevenDaysAgo().lastYearToday, avgPrice: 0 },
          sevenDaysAgo: { date: getLastYearTodayAndSevenDaysAgo().sevenDaysAgo, avgPrice: 0 },
        };
      }
      const priceHistory = priceHistoryCache.get(key);
      const result = buildRoomStatusFromListAndData(roomTypeList, byId, refs, priceHistory);
      setStoreSliceInLocalStorage(hotelId, 'roomStatus', { data: result, fetchedAt: Date.now() });
      return result;
    } catch {
      if (list && list.length > 0 && dataEntry) return buildWithRefs();
      return fallback;
    }
  })().finally(() => { inFlightRoomStatus.delete(key); });
  inFlightRoomStatus.set(key, promise);
  return promise;
}

export function clearRoomStatusCache(hotelId: number | string): void {
  const key = cacheKey(hotelId);
  roomTypeListCache.delete(key);
  roomDataCache.delete(key);
  priceHistoryCache.delete(key);
  const today = getTodayYYYYMMDD();
  avgPriceRefsCache.delete(`avgPriceRefs:${String(hotelId)}:${today}`);
  inFlightRoomStatus.delete(key);
  inFlightAvgRefs.delete(`avgPriceRefs:${String(hotelId)}:${today}`);
}

export function __getRoomStatusCachesForDev(): {
  roomTypeListCache: Map<string, RoomTypeStable[]>;
  roomDataCache: Map<string, RoomDataEntry>;
  avgPriceRefsCache: Map<string, AvgPriceRefs>;
} {
  return { roomTypeListCache, roomDataCache, avgPriceRefsCache };
}
